import { FC } from 'react';
import Chart from 'react-apexcharts';

// --- Types ---
type ChartMetric = 'sales' | 'volume' | 'price';
type ChartViewMode = 'absolute' | 'percentage';

interface ComparativeBarChartProps {
  series: { name: string; data: number[] }[];
  categories: string[];
  title: string;
  metric: ChartMetric;
  viewMode: ChartViewMode;
  originalData?: number[]; // Datos originales con signo para determinar ganador
}

// --- Component ---
const ComparativeBarChart: FC<ComparativeBarChartProps> = ({
  series,
  categories,
  title,
  metric,
  viewMode,
}) => {
  const options: any = {
    chart: {
      type: 'bar',
      height: 400,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 4,
        distributed: false,
      },
    },
    dataLabels: {
      enabled: viewMode === 'percentage',
      formatter: (val: number) => {
        if (viewMode === 'percentage') {
          return `${val.toFixed(1)}%`;
        }
        return '';
      },
      style: {
        fontSize: '12px',
        colors: ['#ffffff'],
        fontWeight: 'bold',
      },
      dropShadow: {
        enabled: true,
        color: '#000000',
        top: 0,
        left: 0,

        opacity: 1,
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      title: { text: title },
      labels: {
        formatter: (val: number) => {
          if (viewMode === 'percentage') {
            return `${val.toFixed(0)}%`;
          }

          // Compact notation for sales and price
          if (metric === 'sales' || metric === 'price') {
            if (!isFinite(val) || isNaN(val) || val === 0) {
              return '0 US$';
            }
            if (val >= 1000000) {
              return (val / 1000000).toFixed(1) + 'M US$';
            } else if (val >= 1000) {
              return (val / 1000).toFixed(0) + 'K US$';
            }
            return val.toFixed(0) + ' US$';
          }

          // Compact notation for volume
          if (metric === 'volume') {
            if (!isFinite(val) || isNaN(val) || val === 0) {
              return '0';
            }
            if (val >= 1000000) {
              return (val / 1000000).toFixed(1) + 'M';
            } else if (val >= 1000) {
              return (val / 1000).toFixed(0) + 'K';
            }
            return val.toFixed(0);
          }

          return new Intl.NumberFormat('es-ES').format(val);
        },
      },
    },
    fill: { opacity: 1 },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => {
          if (viewMode === 'percentage') {
            return `${val.toFixed(2)}%`;
          }
          if (metric === 'price') {
            return new Intl.NumberFormat('es-ES', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(val) + '/kg';
          }
          if (metric === 'sales') {
            return new Intl.NumberFormat('es-ES', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(val);
          }
          return `${new Intl.NumberFormat('es-ES').format(val)} kg`;
        },
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      show: true,
    },
    colors: ['#2563eb', '#16a34a'],
  };

  return <Chart options={options} series={series} type='bar' height={400} />;
};

export default ComparativeBarChart;
